import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { getFeedDetails } from 'Redux/actions/feedActions'
import { getFeedDetails as getFeedDetailsSelector } from 'Redux/selectors/feed'
import {
  ReviewStatus,
  ReviewSelectValue,
  CampaignPlatformTypeEnum,
} from 'Types/index'
import { getUserId } from 'Redux/selectors/auth'
import {
  reviewRequestSeenEvent,
  reviewRequestSeenNotAvailableEvent,
} from 'Helpers/tagManager'
import { TrackDetails } from '../TrackDetails'
import { ReviewTrackForm } from '../ReviewTrackForm'
import { WarningForm } from '../WarningForm'
import { ReviewDetails as ReviewDetailsComponent } from '../ReviewDetails'

import { ReviewTrackProps } from './props'
import {
  Container,
  SectionTitle,
  TrackPreviewContainer,
  TrackPreview,
  OpenSpotify,
  OpenSpotifyLink,
  ButtonContainer,
  Button,
  ReservedLabel,
} from './styles'

export const ReviewTrack: FunctionComponent<ReviewTrackProps> = ({
  campaign_id,
  onSuccess,
  openSkipModal,
  setModalHeaderGreen,
  setIsActualCampaignDetailsLoaded,
  onClose,
}) => {
  const userId = useSelector(getUserId)

  const [threeReviewsWarning, setThreeReviewsWarning] = useState<boolean>(false)
  const [
    playlistFromWarning,
    setPlaylistFromWarning,
  ] = useState<ReviewSelectValue | null>(null)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const reviewDetails = useSelector(getFeedDetailsSelector)

  useEffect(() => {
    dispatch(
      getFeedDetails({
        campaign_id,
        callback: (error, response) => {
          setIsActualCampaignDetailsLoaded(true)
          if (!error) {
            !response.isReserved
              ? reviewRequestSeenEvent(
                  new Date().getTime(),
                  campaign_id,
                  CampaignPlatformTypeEnum.SPOTIFY,
                  userId,
                  true
                )
              : reviewRequestSeenNotAvailableEvent(
                  CampaignPlatformTypeEnum.SPOTIFY,
                  campaign_id,
                  userId,
                  new Date().getTime(),
                  true
                )
          }
        },
      })
    )
    return () => {
      setIsActualCampaignDetailsLoaded(false)
    }
  }, [campaign_id])

  const getTrackId = (url: string) => {
    if (!url) return ''
    const urlParts = url.split('/')
    return urlParts[urlParts.length - 1]
  }

  const isDetailsLoaded = useMemo(() => !!Object.keys(reviewDetails).length, [
    reviewDetails,
  ])

  const handleAction = () => {
    !reviewDetails.isReserved
      ? onClose()
      : openSkipModal({ campaign_id: reviewDetails.campaign_id })
  }

  return (
    <>
      <Container threeReviewsWarning={threeReviewsWarning}>
        <SectionTitle>{t('reviewsPage.listenToTrack')}</SectionTitle>
        {isDetailsLoaded && (
          <TrackPreviewContainer>
            <TrackPreview>
              <iframe
                title="spotify"
                allow="encrypted-media"
                frameBorder="0"
                height="100%"
                src={`https://open.spotify.com/embed/track/${getTrackId(
                  reviewDetails.track?.track_url
                )}`}
                width="100%"
              />
            </TrackPreview>
            <OpenSpotify>
              {t('reviewsPage.orOpenOn')}
              <OpenSpotifyLink
                target="_blank"
                href={`https://open.spotify.com/track/${getTrackId(
                  reviewDetails.track?.track_url
                )}`}
              >
                Spotify
              </OpenSpotifyLink>
            </OpenSpotify>
          </TrackPreviewContainer>
        )}
        {isDetailsLoaded && <TrackDetails reviewDetails={reviewDetails} />}
        {reviewDetails.isReserved ? (
          <ReservedLabel>
            Oops, you`re a bit late. The last review for this track has already
            been submitted
          </ReservedLabel>
        ) : (
          <>
            {reviewDetails.review_status === ReviewStatus.awaiting_review && (
              <ReviewTrackForm
                // @ts-ignore
                playlistFromWarning={playlistFromWarning}
                reviewDetails={reviewDetails}
                openSkipModal={openSkipModal}
                setModalHeaderGreen={setModalHeaderGreen}
                setThreeReviewsWarning={setThreeReviewsWarning}
                closeReviewModal={onClose}
                onSuccess={onSuccess}
              />
            )}
            {reviewDetails.review_status !== ReviewStatus.awaiting_review && (
              <ReviewDetailsComponent reviewDetails={reviewDetails} />
            )}
          </>
        )}
        {(reviewDetails.review_status !== ReviewStatus.awaiting_review ||
          reviewDetails.isReserved) && (
          <ButtonContainer>
            <Button onClick={handleAction}>
              {!reviewDetails.isReserved
                ? t('reviewsPage.closeReview')
                : t('reviewsPage.skipThisReview')}
            </Button>
          </ButtonContainer>
        )}
      </Container>
      {threeReviewsWarning && (
        <WarningForm
          setPlaylistFromWarning={setPlaylistFromWarning}
          setThreeReviewsWarning={setThreeReviewsWarning}
          setModalHeaderGreen={setModalHeaderGreen}
        />
      )}
    </>
  )
}
